import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Flame, Clock, Trophy, ArrowRight, CheckCircle2, XCircle, MinusCircle,
  ChevronLeft, ChevronRight, Calendar, Sparkles, Crown, Medal, Award
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function rankIcon(rank) {
  if (rank === 1) return <Crown className="h-4 w-4 text-gcgold" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-slate-300" />;
  if (rank === 3) return <Award className="h-4 w-4 text-amber-700" />;
  return <span className="font-mono text-sm text-slate-500 font-bold">{rank}</span>;
}

function CountdownToTomorrow() {
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const tomorrow = new Date();
      tomorrow.setUTCHours(24, 0, 0, 0);
      const diff = Math.max(0, tomorrow.getTime() - now.getTime());
      setTime({
        h: Math.floor((diff / (1000 * 60 * 60)) % 24),
        m: Math.floor((diff / (1000 * 60)) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);
  return (
    <span className="font-mono text-sm font-bold text-gcgold tabular-nums">
      {String(time.h).padStart(2, "0")}h {String(time.m).padStart(2, "0")}m {String(time.s).padStart(2, "0")}s
    </span>
  );
}

// ============ INTRO state ============
function ChallengeIntro({ status, streak, onStart }) {
  const navigate = useNavigate();

  if (status?.completed) {
    return (
      <div className="bg-ink-800 border border-emerald-500/30 rounded-3xl p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-5">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 font-mono">Completado</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[0.95]">
            Reto de hoy<br /><span className="text-emerald-400">superado.</span>
          </h2>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Tu nota</div>
              <div className="font-display text-3xl font-extrabold text-gcgold mt-1 tracking-tighter">{status.attempt.score_10}</div>
            </div>
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Aciertos</div>
              <div className="font-display text-3xl font-extrabold text-emerald-400 mt-1 tracking-tighter">{status.attempt.correct}/{status.attempt.total}</div>
            </div>
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Tiempo</div>
              <div className="font-display text-3xl font-extrabold text-white mt-1 tracking-tighter font-mono">{formatTime(status.attempt.elapsed_seconds)}</div>
            </div>
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Racha</div>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="font-display text-3xl font-extrabold text-rose-400 tracking-tighter">{streak?.current_streak ?? 0}</span>
                <Flame className="h-5 w-5 text-rose-400" />
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <div className="text-sm text-slate-400">
              Próximo reto en <CountdownToTomorrow />
            </div>
            <Button onClick={() => navigate("/dashboard")} variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl ml-auto">
              Volver al panel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink-800 border border-gcgold/30 rounded-3xl p-8 lg:p-10 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-gcgold/15 blur-3xl" />
      <div className="absolute inset-0 bg-dot-grid-dense opacity-30" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gcgold/10 border border-gcgold/30 mb-5">
          <Sparkles className="h-3.5 w-3.5 text-gcgold" />
          <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">Reto diario</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[0.95]">
          10 preguntas.<br /><span className="text-gcgold">10 minutos.</span>
        </h1>
        <p className="mt-5 text-base text-slate-400 max-w-xl">
          Cada día, las mismas 10 preguntas para todos los opositores. El que más acierta y más rápido lo hace, lidera el ranking. Construye tu racha.
        </p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Preguntas</div>
            <div className="font-display text-3xl font-extrabold text-white mt-1 tracking-tighter">{status?.total_questions ?? 10}</div>
          </div>
          <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Tiempo</div>
            <div className="font-display text-3xl font-extrabold text-white mt-1 tracking-tighter">{status?.duration_minutes ?? 10} min</div>
          </div>
          <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Hoy</div>
            <div className="font-display text-3xl font-extrabold text-emerald-400 mt-1 tracking-tighter">{status?.participants_today ?? 0}</div>
            <div className="text-[10px] text-slate-500 font-mono">participantes</div>
          </div>
          <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Tu racha</div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="font-display text-3xl font-extrabold text-rose-400 tracking-tighter">{streak?.current_streak ?? 0}</span>
              <Flame className="h-5 w-5 text-rose-400" />
            </div>
          </div>
        </div>

        <Button
          onClick={onStart}
          className="mt-10 bg-gcgold hover:bg-gcgold-light text-ink font-bold h-14 px-10 rounded-xl shadow-[0_0_60px_-10px_rgba(212,175,55,0.8)] group text-base"
          data-testid="start-daily-challenge-button"
        >
          Empezar el reto <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
        <p className="mt-3 text-[10px] text-slate-600 font-mono uppercase tracking-[0.22em]">
          ⚠ Solo tienes una oportunidad al día — sin pausas ni reintentos
        </p>
      </div>
    </div>
  );
}

// ============ RUNNING state ============
function ChallengeRunner({ session, onSubmit }) {
  const questions = session.questions || [];
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const i = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(i);
  }, []);

  const totalSeconds = (session.duration_minutes || 10) * 60;
  const remaining = Math.max(0, totalSeconds - elapsed);
  const isCritical = remaining < 30;
  const q = questions[current];
  const progress = ((current + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  const submit = () => {
    onSubmit({
      answers: questions.map((qq) => ({ question_id: qq.id, selected_index: answers[qq.id] ?? null })),
      elapsed_seconds: elapsed,
    });
  };

  useEffect(() => {
    if (remaining === 0 && !confirmOpen) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  return (
    <>
      <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 glass-header mb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-400 font-mono">
            <Sparkles className="h-3.5 w-3.5 text-gcgold" />
            <span className="text-white font-bold">{current + 1}</span><span className="text-slate-600">/</span><span>{questions.length}</span>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono text-sm font-bold border transition-colors ${
            isCritical ? "bg-red-950/60 text-red-400 border-red-700/40 animate-pulse" : "bg-white/5 text-white border-white/10"
          }`}>
            <Clock className="h-3.5 w-3.5" />{formatTime(remaining)}
          </div>
          <Button onClick={() => setConfirmOpen(true)} variant="outline" className="bg-gcgold/10 border-gcgold/30 text-gcgold hover:bg-gcgold hover:text-ink rounded-xl">Finalizar</Button>
        </div>
        <div className="h-1 mt-2 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-gcgreen to-gcgold transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="bg-ink-800 border border-white/5 rounded-3xl p-6 sm:p-10">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono mb-4">Pregunta {current + 1}</div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight" data-testid="daily-question">
          {q?.question}
        </h2>

        <div className="mt-8 space-y-2.5">
          {q?.options?.map((opt, idx) => {
            const sel = answers[q.id] === idx;
            const letter = String.fromCharCode(65 + idx);
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setAnswers((a) => ({ ...a, [q.id]: idx }))}
                className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  sel ? "border-gcgold/50 bg-gcgold/10 shadow-[0_0_30px_-8px_rgba(212,175,55,0.5)]"
                  : "border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
                }`}
                data-testid={`daily-option-${idx}`}
              >
                <span className={`h-9 w-9 flex items-center justify-center rounded-lg font-mono text-sm font-bold flex-shrink-0 ${
                  sel ? "bg-gcgold text-ink" : "bg-white/5 text-slate-400 border border-white/10"
                }`}>{letter}</span>
                <span className="text-sm sm:text-base text-slate-200 flex-1">{opt}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button variant="outline" onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}
            className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
            <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>
          {current < questions.length - 1 ? (
            <Button onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
              className="bg-gcgreen hover:bg-gcgreen-light text-white rounded-xl">
              Siguiente <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => setConfirmOpen(true)}
              className="bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl"
              data-testid="daily-submit-button">
              Enviar reto
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-ink-800 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Enviar el reto?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Has respondido {answeredCount} de {questions.length} preguntas. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white">Seguir</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-gcgold hover:bg-gcgold-light text-ink font-bold">Enviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ============ RESULT state ============
function ChallengeResult({ result, onClose }) {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const passed = result.score_10 >= 5;

  useEffect(() => {
    api.get("/daily-challenge/leaderboard?limit=10").then((r) => setLeaderboard(r.data.rows || [])).catch(console.error);
  }, []);

  const accuracy = result.total ? Math.round((result.correct / result.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`relative overflow-hidden rounded-3xl p-8 lg:p-10 ${
          passed ? "bg-gradient-to-br from-gcgreen via-ink-700 to-ink-800 border border-gcgold/40" : "bg-ink-800 border border-white/10"
        }`}
      >
        <div className="absolute inset-0 bg-dot-grid opacity-20" />
        {passed && <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-gcgold/30 blur-3xl" />}
        <div className="relative">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono mb-3">Reto diario · Tu posición</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-7xl lg:text-8xl font-extrabold text-white tracking-tighter">#{result.rank}</span>
            <span className="font-mono text-base text-slate-400">de {result.total_participants}</span>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Nota</div>
              <div className="font-display text-3xl font-extrabold text-gcgold mt-1 tracking-tighter">{result.score_10}</div>
            </div>
            <div className="bg-ink-700/80 border border-emerald-700/30 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400 font-mono">Aciertos</div>
              <div className="font-display text-3xl font-extrabold text-emerald-400 mt-1 tracking-tighter">{result.correct}</div>
            </div>
            <div className="bg-ink-700/80 border border-red-700/30 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-red-400 font-mono">Errores</div>
              <div className="font-display text-3xl font-extrabold text-red-400 mt-1 tracking-tighter">{result.wrong}</div>
            </div>
            <div className="bg-ink-700/80 border border-white/5 rounded-xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 font-mono">Tiempo</div>
              <div className="font-display text-3xl font-extrabold text-white mt-1 tracking-tighter font-mono">{formatTime(result.elapsed_seconds)}</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Daily leaderboard */}
      <div className="bg-ink-800 border border-white/5 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <Trophy className="h-4 w-4 text-gcgold" /> Ranking de hoy
          </h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500">{leaderboard.length} primeros</span>
        </div>
        <div className="divide-y divide-white/5" data-testid="daily-leaderboard">
          {leaderboard.length === 0 && <div className="p-6 text-sm text-slate-500">Aún no hay datos.</div>}
          {leaderboard.map((r) => {
            const isMe = r.user_id === result.user_id;
            return (
              <div key={r.user_id} className={`px-6 py-3 flex items-center gap-3 ${isMe ? "bg-gcgold/10" : "hover:bg-white/[0.02]"}`}>
                <div className="w-8 flex items-center justify-center">{rankIcon(r.rank)}</div>
                <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                  <span className={`text-sm font-medium ${isMe ? "text-gcgold font-bold" : "text-white"}`}>{r.user_name}</span>
                  {isMe && <span className="text-[9px] uppercase tracking-[0.22em] font-mono font-bold text-gcgold bg-gcgold/15 px-1.5 py-0.5 rounded">Tú</span>}
                  <span className="ml-auto font-mono text-xs text-slate-500">{formatTime(r.elapsed_seconds)}</span>
                </div>
                <div className="font-display text-xl font-extrabold text-gcgold tracking-tighter w-12 text-right">{r.score_10}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button asChild className="bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl">
          <Link to="/dashboard">Volver al panel</Link>
        </Button>
        <Button asChild variant="outline" className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl">
          <Link to="/comunidad">Compartir en la comunidad <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>

      {/* Review */}
      <div className="bg-ink-800 border border-white/5 rounded-3xl p-6">
        <h3 className="font-display text-lg font-bold text-white mb-4">Revisión</h3>
        <div className="space-y-3">
          {result.items.map((item, idx) => {
            const isBlank = item.selected_index === null || item.selected_index === undefined;
            const isCorrect = item.is_correct;
            return (
              <div key={item.question_id} className={`border rounded-2xl p-4 ${
                isBlank ? "border-white/5 bg-white/[0.02]"
                : isCorrect ? "border-emerald-700/30 bg-emerald-950/20"
                : "border-red-700/30 bg-red-950/20"
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs font-mono font-bold flex-shrink-0 ${
                    isBlank ? "bg-white/5 text-slate-500" : isCorrect ? "bg-emerald-900/40 text-emerald-300" : "bg-red-900/40 text-red-300"
                  }`}>{idx + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{item.question}</div>
                    <div className="mt-2 text-xs text-slate-400">
                      Correcta: <span className="text-emerald-400 font-bold">{String.fromCharCode(65 + item.correct_index)}</span> · {item.options[item.correct_index]}
                    </div>
                    {item.explanation && (
                      <div className="mt-2 text-xs text-slate-500 border-l-2 border-gcgold/40 pl-3 py-1 bg-gcgold/5 rounded-r">
                        {item.explanation}
                      </div>
                    )}
                  </div>
                  {isBlank ? <MinusCircle className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  : isCorrect ? <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                  : <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function DailyChallenge() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [streak, setStreak] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | running | result
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const [s, st] = await Promise.all([api.get("/daily-challenge"), api.get("/daily-challenge/streak")]);
      setStatus(s.data); setStreak(st.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refresh(); }, []);

  const onStart = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/daily-challenge/start");
      setSession(data); setPhase("running");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      refresh();
    } finally { setLoading(false); }
  };

  const onSubmit = async (payload) => {
    try {
      const { data } = await api.post("/daily-challenge/submit", payload);
      data.user_id = user?.id; // ensure for ranking highlight
      setResult(data); setPhase("result");
      refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Reto diario" path="/reto-diario" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />
      <div className="relative">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" /> {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </div>

          {err && <Alert variant="destructive" className="mb-6 border-red-900/50 bg-red-950/40 text-red-200"><AlertDescription>{err}</AlertDescription></Alert>}

          {phase === "intro" && status && <ChallengeIntro status={status} streak={streak} onStart={onStart} />}
          {phase === "running" && session && <ChallengeRunner session={session} onSubmit={onSubmit} />}
          {phase === "result" && result && <ChallengeResult result={result} />}

          {loading && phase === "intro" && (
            <div className="mt-4 text-center text-sm text-slate-500">Preparando reto...</div>
          )}
        </main>
        {phase !== "running" && <Footer />}
      </div>
    </div>
  );
}
